import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/utils";
import { Portal } from "./Portal";

/**
 * Modal
 *
 * A centred overlay panel. Handles the interaction contract expected of a
 * dialog: closes on Escape and backdrop click, moves focus into the panel on
 * open, restores focus to the trigger on close, and marks the panel with
 * `role="dialog"` / `aria-modal`.
 *
 * It is unopinionated about content — {@link Dialog} composes it into a
 * confirm/cancel pattern.
 *
 * @example
 *   <Modal open={open} onClose={() => setOpen(false)} title="Rename">…</Modal>
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  /** Max width of the panel. */
  size?: "sm" | "md" | "lg";
  /** Hide the top-right close affordance. */
  hideClose?: boolean;
}

const sizes = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
} as const;

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  hideClose = false,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // Defer so the panel exists before we move focus.
    const raf = requestAnimationFrame(() => panelRef.current?.focus());

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-modal flex items-center justify-center p-6"
        role="presentation"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-overlay-in"
          onClick={onClose}
          aria-hidden
        />
        {/* Panel */}
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          className={cn(
            "relative w-full rounded-lg border border-border-strong bg-bg-overlay",
            "shadow-modal animate-scale-in focus:outline-none",
            sizes[size],
          )}
        >
          {(title || !hideClose) && (
            <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
              <div className="flex flex-col gap-0.5">
                {title && (
                  <h2 className="text-md font-semibold text-text-primary">
                    {title}
                  </h2>
                )}
                {description && (
                  <p className="text-sm text-text-secondary">{description}</p>
                )}
              </div>
              {!hideClose && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="no-drag -mr-1 -mt-0.5 flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-bg-hover hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}

          {children && <div className="px-4 py-4 selectable">{children}</div>}

          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
              {footer}
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
